/**
 * Salary Adjustment Dialog — issues a supplemental payslip when an
 * employee's salary changes retroactively (e.g. mid-cycle raise).
 *
 * Two steps:
 *   1. Enter new salary + effective date + reason → preview delta.
 *   2. Review preview (with checkboxes for future affected cycles) →
 *      commit. Master record is updated so future payrolls use the new
 *      salary automatically.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { SpinnerGap, CurrencyDollar } from '@phosphor-icons/react';
import EmployeeSearchSelect from './EmployeeSearchSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  ar: {
    title: 'إصدار قسيمة تعديل راتب',
    desc:  'أصدر قسيمة تكميلية عندما تتغيّر بيانات الموظف المالية بأثر رجعي (مثال: زيادة راتب في منتصف الشهر).',
    employee: 'الموظف',
    pickEmployee: 'اختر الموظف',
    effectiveDate: 'تاريخ سريان التعديل',
    reason: 'السبب / الملاحظات',
    reasonPh: 'مثال: زيادة سنوية 2026',
    currentSalary: 'الراتب الحالي',
    newSalary: 'الراتب الجديد',
    basic: 'الأساسي',
    housing: 'بدل السكن',
    transport: 'بدل النقل',
    others: 'بدلات أخرى',
    fullMonthly: 'إجمالي الشهر',
    preview: 'معاينة الفارق',
    computing: 'جاري الحساب…',
    currentCycle: 'الدورة الحالية',
    days: 'أيام',
    delta: 'الفارق المستحق',
    futureCycles: 'دورات سابقة منشورة تحتوي أياماً بالسعر القديم',
    includeCycle: 'ضمّن هذه الدورة',
    total: 'إجمالي التعديل',
    updateMaster: 'تحديث بيانات الموظف الأساسية (سيسري على المسيرات القادمة تلقائياً)',
    commit: 'إصدار قسيمة التعديل',
    committing: 'جاري الإصدار…',
    back: 'رجوع',
    cancel: 'إلغاء',
    noDiff: 'لا يوجد فرق مستحق بهذه القيم — راجع تاريخ السريان أو القيم الجديدة.',
  },
  en: {
    title: 'Issue Salary Adjustment Payslip',
    desc:  'Issue a supplemental payslip when an employee\'s salary changes retroactively (e.g. mid-cycle raise).',
    employee: 'Employee',
    pickEmployee: 'Select employee',
    effectiveDate: 'Effective Date',
    reason: 'Reason / Notes',
    reasonPh: 'e.g. Annual raise 2026',
    currentSalary: 'Current Salary',
    newSalary: 'New Salary',
    basic: 'Basic',
    housing: 'Housing',
    transport: 'Transport',
    others: 'Other',
    fullMonthly: 'Total Monthly',
    preview: 'Preview Delta',
    computing: 'Computing…',
    currentCycle: 'Current Cycle',
    days: 'days',
    delta: 'Delta Amount',
    futureCycles: 'Prior published cycles that contain days at the old rate',
    includeCycle: 'Include this cycle',
    total: 'Total Adjustment',
    updateMaster: 'Update employee master record (future payrolls will use the new salary automatically)',
    commit: 'Issue Adjustment Payslip',
    committing: 'Issuing…',
    back: 'Back',
    cancel: 'Cancel',
    noDiff: 'No delta payable with these values — check the effective date or new amounts.',
  },
};

export default function SalaryAdjustmentDialog({ open, onOpenChange, employees, language, direction, onSuccess }) {
  const isAr = language === 'ar';
  const L = isAr ? T.ar : T.en;

  const [step, setStep] = useState(1);
  const [userId, setUserId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [newBasic, setNewBasic] = useState('');
  const [newHousing, setNewHousing] = useState('');
  const [newTransport, setNewTransport] = useState('');
  const [newOthers, setNewOthers] = useState('');
  const [reason, setReason] = useState('');
  const [updateMaster, setUpdateMaster] = useState(true);

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedCycles, setSelectedCycles] = useState({});
  const [committing, setCommitting] = useState(false);

  // Reset ALL form state whenever the dialog is opened. v1.5.23 fix —
  // previously only step/preview/selectedCycles were cleared, so
  // reopening the dialog for a different employee showed the previous
  // employee's cached salary values.
  useEffect(() => {
    if (open) {
      setStep(1);
      setPreview(null);
      setSelectedCycles({});
      setUserId('');
      setEffectiveDate(new Date().toISOString().slice(0, 10));
      setNewBasic('');
      setNewHousing('');
      setNewTransport('');
      setNewOthers('');
      setReason('');
      setUpdateMaster(true);
    }
  }, [open]);

  // Auto-fill new-salary fields from picked employee (as starting point)
  useEffect(() => {
    const emp = employees.find(e => (e.id || e._id) === userId);
    if (emp && !newBasic) {
      setNewBasic(String(emp.basic_salary || ''));
      setNewHousing(String(emp.housing_allowance || ''));
      setNewTransport(String(emp.transport_allowance || ''));
      setNewOthers(String(emp.other_allowances || ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const doPreview = async () => {
    if (!userId || !effectiveDate || !newBasic) {
      toast.error(isAr ? 'يرجى تعبئة الحقول المطلوبة' : 'Please fill required fields');
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await axios.post(`${API}/payroll/adjustment/preview`, {
        user_id: userId,
        effective_date: effectiveDate,
        new_basic: Number(newBasic),
        new_housing: Number(newHousing || 0),
        new_transport: Number(newTransport || 0),
        new_others: Number(newOthers || 0),
        reason,
      }, { withCredentials: true });
      setPreview(data);
      // Pre-select all future cycles with delta > 0
      const preSel = {};
      (data.future_cycles || []).forEach(fc => { preSel[fc.id] = true; });
      setSelectedCycles(preSel);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشلت المعاينة' : 'Preview failed'));
    } finally {
      setPreviewing(false);
    }
  };

  const computedTotal = (() => {
    if (!preview) return 0;
    let t = preview.current_cycle?.delta || 0;
    (preview.future_cycles || []).forEach(fc => {
      if (selectedCycles[fc.id]) t += fc.delta;
    });
    return Math.round(t * 100) / 100;
  })();

  const doCommit = async () => {
    if (computedTotal === 0) {
      toast.error(L.noDiff);
      return;
    }
    setCommitting(true);
    try {
      const selectedIds = Object.keys(selectedCycles).filter(k => selectedCycles[k]);
      const { data } = await axios.post(`${API}/payroll/adjustment`, {
        user_id: userId,
        effective_date: effectiveDate,
        new_basic: Number(newBasic),
        new_housing: Number(newHousing || 0),
        new_transport: Number(newTransport || 0),
        new_others: Number(newOthers || 0),
        reason,
        selected_cycle_ids: selectedIds,
        update_master: updateMaster,
      }, { withCredentials: true });
      toast.success(isAr
        ? `تم إنشاء قسيمة التعديل كمسودة بمبلغ ${data.total.toLocaleString()} ر.س — اضغط "إرسال" في الجدول لإرسالها للموظف.`
        : `Adjustment draft created for ${data.total.toLocaleString()} SAR — click "Send" on the row to notify the employee.`);
      onOpenChange(false);
      if (onSuccess) onSuccess(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل الإصدار' : 'Commit failed'));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" dir={direction}>
        <DialogHeader>
          <DialogTitle data-testid="salary-adj-title" className="flex items-center gap-2">
            <CurrencyDollar size={20} weight="duotone" />
            {L.title}
          </DialogTitle>
          <DialogDescription>{L.desc}</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label>{L.employee}</Label>
              <EmployeeSearchSelect
                employees={employees}
                value={userId}
                onChange={setUserId}
                language={language}
                direction={direction}
                placeholder={L.pickEmployee}
                filterFn={(e) => e.role === 'employee'}
                testId="adj-employee"
              />
            </div>

            <div>
              <Label>{L.effectiveDate}</Label>
              <Input
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                data-testid="adj-effective-date"
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label>{L.basic}</Label>
                <Input type="number" step="0.01" value={newBasic} onChange={(e) => setNewBasic(e.target.value)} data-testid="adj-new-basic" />
              </div>
              <div>
                <Label>{L.housing}</Label>
                <Input type="number" step="0.01" value={newHousing} onChange={(e) => setNewHousing(e.target.value)} data-testid="adj-new-housing" />
              </div>
              <div>
                <Label>{L.transport}</Label>
                <Input type="number" step="0.01" value={newTransport} onChange={(e) => setNewTransport(e.target.value)} data-testid="adj-new-transport" />
              </div>
              <div>
                <Label>{L.others}</Label>
                <Input type="number" step="0.01" value={newOthers} onChange={(e) => setNewOthers(e.target.value)} data-testid="adj-new-others" />
              </div>
            </div>

            <div>
              <Label>{L.reason}</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={L.reasonPh}
                data-testid="adj-reason"
                rows={2}
              />
            </div>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-4 text-sm">
            {/* Salary snapshot */}
            <div className="grid grid-cols-2 gap-3 p-3 bg-[#F2F0FA] rounded">
              <div>
                <div className="font-semibold text-[#655dc6]">{L.currentSalary}</div>
                <div>{L.fullMonthly}: <b>{preview.current_snapshot.full_monthly.toLocaleString()}</b></div>
              </div>
              <div>
                <div className="font-semibold text-emerald-700">{L.newSalary}</div>
                <div>{L.fullMonthly}: <b>{preview.new_snapshot.full_monthly.toLocaleString()}</b></div>
              </div>
            </div>

            {/* Current cycle */}
            <div className="border border-[#C4BFF0] rounded p-3">
              <div className="font-semibold mb-1">{L.currentCycle} — {preview.current_cycle.month}/{preview.current_cycle.year}</div>
              <div className="text-xs text-muted-foreground mb-2">
                {preview.current_cycle.cycle_start} → {preview.current_cycle.cycle_end}
              </div>
              <div className="flex justify-between">
                <span>{preview.current_cycle.days_at_new} {L.days}</span>
                <span className="font-bold text-emerald-700" data-testid="adj-current-delta">
                  +{preview.current_cycle.delta.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Future cycles list */}
            {preview.future_cycles && preview.future_cycles.length > 0 && (
              <div>
                <div className="font-semibold mb-2">{L.futureCycles}</div>
                <div className="space-y-2">
                  {preview.future_cycles.map(fc => (
                    <label key={fc.id} className="flex items-center gap-3 p-2 border border-[#C4BFF0] rounded cursor-pointer hover:bg-[#F2F0FA]">
                      <Checkbox
                        checked={!!selectedCycles[fc.id]}
                        onCheckedChange={(v) => setSelectedCycles(prev => ({ ...prev, [fc.id]: !!v }))}
                        data-testid={`adj-cycle-${fc.id}`}
                      />
                      <div className="flex-1 flex justify-between">
                        <span>{fc.month}/{fc.year} — {fc.days_at_new} {L.days}</span>
                        <span className="font-bold text-emerald-700">+{fc.delta.toLocaleString()}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Total */}
            <div className="p-3 bg-emerald-50 border border-emerald-300 rounded flex justify-between items-center">
              <span className="font-semibold">{L.total}</span>
              <span className="text-lg font-bold text-emerald-700" data-testid="adj-total">
                {computedTotal.toLocaleString()} {isAr ? 'ر.س' : 'SAR'}
              </span>
            </div>

            {/* Master update toggle */}
            <label className="flex items-start gap-3 p-3 border border-[#C4BFF0] rounded cursor-pointer">
              <Checkbox
                checked={updateMaster}
                onCheckedChange={(v) => setUpdateMaster(!!v)}
                data-testid="adj-update-master"
              />
              <span className="text-xs">{L.updateMaster}</span>
            </label>
          </div>
        )}

        <DialogFooter>
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>{L.cancel}</Button>
              <Button onClick={doPreview} disabled={previewing} data-testid="adj-preview-btn">
                {previewing ? <><SpinnerGap className="animate-spin" size={16} /> {L.computing}</> : L.preview}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>{L.back}</Button>
              <Button
                onClick={doCommit}
                disabled={committing || computedTotal === 0}
                data-testid="adj-commit-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {committing ? <><SpinnerGap className="animate-spin" size={16} /> {L.committing}</> : L.commit}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
